import { isObject } from '@mimir/primitive/type-guards/isObject';

export interface TimedMdfUsageRule {
  identifier: {
    key: string;
    label: string;
  };
  contexts: UsageRuleTimingContext[];
}

export const restrictedMaterialIcon = 'warning';
export const detectedTextMaterialIcon = 'short_text';

export const dateOffsetTypes = <const>['ingest_time', 'absolute', 'forever'];

export type UsageRuleDateRange = {
  offsetFrom: typeof dateOffsetTypes[0];
  untilNumberOfDays: number;
  absoluteDate?: never;
} | {
  offsetFrom: typeof dateOffsetTypes[1];
  absoluteDate: string; // ISO date string
  untilNumberOfDays?: never;
} | {
  offsetFrom: typeof dateOffsetTypes[2];
  absoluteDate?: never;
  untilNumberOfDays?: never;
};

export const warningLevels = <const>['always', 'upon_expiry', 'never'];
export type WarningLevel = typeof warningLevels[number];


export interface UsageRuleTimingContext {
  name: string;
  validity: {
    from: UsageRuleDateRange;
    to: UsageRuleDateRange;
  };
  warningLevel: WarningLevel | undefined;
}


export const isInstanceOfUsageRuleTimingContext = (obj: unknown): obj is UsageRuleTimingContext => {
  if (!isObject(obj)) return false;
  const { name, validity, warningLevel } = obj as Partial<UsageRuleTimingContext>;
  return !!(
    name && typeof name === 'string'
    && isInstanceOfUsageRuleDateRange(validity?.from) && isInstanceOfUsageRuleDateRange(validity?.to)
    && ((warningLevel && warningLevels.includes(warningLevel)) || !warningLevel)
  );
};


export const isInstanceOfUsageRuleDateRange = (obj: unknown): obj is UsageRuleDateRange => {
  if (!isObject(obj)) return false;
  const { offsetFrom, untilNumberOfDays, absoluteDate } = obj as Partial<UsageRuleDateRange>;
  const isValidOffset = offsetFrom && dateOffsetTypes.includes(offsetFrom);
  if (!isValidOffset) return false;

  switch (offsetFrom) {
    case 'ingest_time':
      return typeof untilNumberOfDays === 'number' && untilNumberOfDays > -1;
    case 'absolute':
      return !!absoluteDate && typeof absoluteDate === 'string';
    case 'forever':
      return true;
    default:
      return false;
  }
};

